// Global function for inline onclick handlers - available immediately
let backNavigationInstance = null;
const pendingCalls = [];
const BOTTOM_NAV_MOBILE_BREAKPOINT = "(max-width: 767px)";
let bottomNavMediaQuery = null;
let bottomNavHeightFrame = null;

// localStorage keys
const HISTORY_STACK_KEY = 'historyStack';
const INITIAL_URL_KEY = 'initialUrl';
const LAST_ORDERS_PAGE_KEY = 'lastOrdersPage';
const HISTORY_STACK_MAX = 50;
const HISTORY_STACK_COMPARISON_IGNORE_PARAMS = ['city'];
const DELIVERY_LOCATION_NAVIGATION_SIGNAL_KEY = 'deliveryLocationNavigationResolutionSignal';
const DELIVERY_LOCATION_NAVIGATION_SIGNAL_TTL_MS = 30000;
const OVERLAY_HISTORY_STATE_KEY = '__bottomNavOverlay';
const OVERLAY_HISTORY_TYPES = {
  CART_DRAWER: 'cart-drawer',
  MOBILE_MENU: 'mobile-menu',
};

// Some embedded browsers expose localStorage as null or throw on access.
// Keep navigation state best-effort so storage policy cannot break the page.
const localStorage = {
  getItem(key) {
    try { return window.localStorage?.getItem(key) ?? null; } catch (_) { return null; }
  },
  setItem(key, value) {
    try { window.localStorage?.setItem(key, value); } catch (_) {}
  },
  removeItem(key) {
    try { window.localStorage?.removeItem(key); } catch (_) {}
  },
};

function setBottomNavHeight(height) {
  document.documentElement.style.setProperty(
    "--bottom-nav-height",
    `${height}px`,
  );
}

function updateBottomNavHeight() {
  const bottomNav = document.getElementById("page-bottom-nav");

  if (!bottomNav) {
    setBottomNavHeight(0);
    return;
  }

  if (bottomNavMediaQuery && !bottomNavMediaQuery.matches) {
    setBottomNavHeight(0);
    return;
  }

  setBottomNavHeight(bottomNav.offsetHeight || 0);
}

function scheduleBottomNavHeightUpdate() {
  if (bottomNavHeightFrame !== null) return;

  bottomNavHeightFrame = window.requestAnimationFrame(function () {
    bottomNavHeightFrame = null;
    updateBottomNavHeight();
  });
}

window.handleBackNavigation = function() {
  if (backNavigationInstance && typeof backNavigationInstance.handleBackNavigation === 'function') {
    backNavigationInstance.handleBackNavigation();
  } else {
    // Queue the call if handler not ready yet
    pendingCalls.push(true);
  }
};

document.addEventListener("DOMContentLoaded", function () {
  bottomNavMediaQuery = window.matchMedia(BOTTOM_NAV_MOBILE_BREAKPOINT);
  updateBottomNavHeight();
  window.addEventListener("load", scheduleBottomNavHeightUpdate, { once: true });
  window.addEventListener("resize", scheduleBottomNavHeightUpdate);

  if (typeof bottomNavMediaQuery.addEventListener === "function") {
    bottomNavMediaQuery.addEventListener("change", scheduleBottomNavHeightUpdate);
  } else if (typeof bottomNavMediaQuery.addListener === "function") {
    bottomNavMediaQuery.addListener(scheduleBottomNavHeightUpdate);
  }

  const currentPage = window.location.pathname + window.location.search;
  const navItems = document.querySelectorAll(".custom-bottom-nav li");
  let isSelected = false;

  navItems.forEach(function (item) {
    const link = item.querySelector("a");
    if (
      link &&
      (link.getAttribute("href") === currentPage ||
        (link.getAttribute("href") ===
          "https://cultstore.com/pages/orders?page=0" &&
          currentPage === "/pages/orders?page=0"))
    ) {
      item.classList.add("selected");
      isSelected = true;
    }
  });

  if (!isSelected) {
    const homeItem = document.querySelector(
      '.custom-bottom-nav li a[href="/"]',
    );
    if (homeItem) {
      homeItem.parentElement.classList.add("selected");
    }
  }

  // initialUrl and historyStack are now managed in the BackNavigationHandler constructor
  // to avoid the timing issue where DOMContentLoaded fires after the instance is created.
});

class BackNavigationHandler {
  constructor() {
    this.neutralizeBackEnabled = false;
    this._neutralizeCallback = null;
    this._ignoreNextPopstate = false;
    this.lastCapturedUrl = '';
    this._reactCartOpen = false;
    this._overlayHistoryType = null;
    this._overlayHistorySkipReleaseType = null;
    this._mobileMenuOpen = false;

    // Convert same-pathname pushState calls (AJAX filter system, orders pagination)
    // to replaceState so they never add ghost entries to browser history.
    // Ghost entries are same-document entries at the same pathname — navigating
    // back through them keeps the user stuck on the same page.
    // A guard flag (_ajaxPushCountEnabled) lets our own internal pushState calls
    // (neutralizeBack, cart intercept) pass through as real pushState entries,
    // since those intentionally need an entry that can be popped later.
    this._ajaxPushCountEnabled = true;
    this._backFallbackTimer = null;

    const origPushState = window.history.pushState.bind(window.history);
    const origReplaceState = window.history.replaceState.bind(window.history);
    window.history.pushState = (state, title, url) => {
      let result;

      // Only demote pushState calls that carry main.js's AJAX-filter signature
      // ({ path: fullUrl }).  All other callers (marmeto orders pagination, SSO
      // login trap, etc.) use a different state shape and must go through as
      // real pushState entries so their own popstate handlers work correctly.
      if (this._ajaxPushCountEnabled && url && state?.path) {
        try {
          const target = new URL(url, window.location.href);
          if (target.pathname === window.location.pathname) {
            // Demote to replaceState — URL updates without adding a ghost entry.
            result = origReplaceState(state, title, url);
            this.syncCurrentUrlInHistoryStack(url);
            return result;
          }
        } catch (_) {}
      }

      result = origPushState(state, title, url);
      this.syncCurrentUrlInHistoryStack(url);
      return result;
    };

    window.history.replaceState = (state, title, url) => {
      const result = origReplaceState(state, title, url);
      this.syncCurrentUrlInHistoryStack(url);
      return result;
    };

    // Clear any pending fallback timer on every page show (initial load and
    // bfcache restore), since a successful navigation makes the timer a no-op.
    window.addEventListener('pageshow', () => {
      clearTimeout(this._backFallbackTimer);
      this._backFallbackTimer = null;

      if (window.location.pathname === '/') {
        this.resetHistoryState(window.location.href);
        return;
      }

      this.syncCurrentUrlInHistoryStack(window.location.href);
    });

    // initializeHistoryStack sets localStorage.initialUrl synchronously,
    // so this.initialUrl is always correct even on first page load.
    this.initializeHistoryStack();

    this.isWebview = this.detectWebview();
    this.setupEventListeners();

    // Keep the current entry fresh for webview sessions as well.
    if (this.isWebview) {
      this.startUrlWatcher();
    }
  }

  normalizeSameOriginUrl(url) {
    try {
      const normalizedUrl = new URL(url || window.location.href, window.location.href);
      if (normalizedUrl.origin !== window.location.origin) return null;
      return normalizedUrl.toString();
    } catch (_) {
      return null;
    }
  }

  buildRelativeUrl(url) {
    const normalizedUrl = this.normalizeSameOriginUrl(url);
    if (!normalizedUrl) return null;

    const targetUrl = new URL(normalizedUrl);
    return targetUrl.pathname + targetUrl.search + targetUrl.hash;
  }

  markPreserveStoredCityOnNextLoad(url) {
    const targetUrl = this.buildRelativeUrl(url);
    if (!targetUrl) return;

    try {
      window.sessionStorage.setItem(
        DELIVERY_LOCATION_NAVIGATION_SIGNAL_KEY,
        JSON.stringify({
          strategy: 'preferStored',
          targetUrl: targetUrl,
          expiresAt: Date.now() + DELIVERY_LOCATION_NAVIGATION_SIGNAL_TTL_MS,
        }),
      );
    } catch (_) {
      // no-op when storage is blocked
    }
  }

  navigateWithPreservedDeliveryCity(url, options) {
    const config = options || {};
    this.markPreserveStoredCityOnNextLoad(url);

    if (config.replace) {
      window.location.replace(url);
      return;
    }

    window.location.href = url;
  }

  sanitizeHistoryStack(stack) {
    const sourceStack = Array.isArray(stack) ? stack : [];
    const sanitizedStack = [];

    sourceStack.forEach((entry) => {
      const normalizedEntry = this.normalizeSameOriginUrl(entry);
      if (!normalizedEntry) return;
      if (sanitizedStack[sanitizedStack.length - 1] === normalizedEntry) return;
      sanitizedStack.push(normalizedEntry);
    });

    if (sanitizedStack.length > HISTORY_STACK_MAX) {
      sanitizedStack.splice(0, sanitizedStack.length - HISTORY_STACK_MAX);
    }

    return sanitizedStack;
  }

  readHistoryStack() {
    const rawStack = localStorage.getItem(HISTORY_STACK_KEY);
    let parsedStack = [];

    try {
      parsedStack = JSON.parse(rawStack || '[]');
    } catch (_) {
      parsedStack = [];
    }

    const sanitizedStack = this.sanitizeHistoryStack(parsedStack);
    const serializedStack = JSON.stringify(sanitizedStack);

    if (sanitizedStack.length > 0) {
      if (rawStack !== serializedStack) {
        localStorage.setItem(HISTORY_STACK_KEY, serializedStack);
      }
    } else if (rawStack) {
      localStorage.removeItem(HISTORY_STACK_KEY);
    }

    return sanitizedStack;
  }

  writeHistoryStack(stack) {
    const sanitizedStack = this.sanitizeHistoryStack(stack);

    if (sanitizedStack.length > 0) {
      localStorage.setItem(HISTORY_STACK_KEY, JSON.stringify(sanitizedStack));
    } else {
      localStorage.removeItem(HISTORY_STACK_KEY);
    }

    return sanitizedStack;
  }

  buildComparableUrl(url) {
    const normalizedUrl = this.normalizeSameOriginUrl(url);
    if (!normalizedUrl) return null;

    const comparableUrl = new URL(normalizedUrl);
    HISTORY_STACK_COMPARISON_IGNORE_PARAMS.forEach((param) => {
      comparableUrl.searchParams.delete(param);
    });

    const sortedSearchParams = Array.from(comparableUrl.searchParams.entries()).sort(
      ([firstKey, firstValue], [secondKey, secondValue]) => {
        if (firstKey === secondKey) {
          return firstValue.localeCompare(secondValue);
        }
        return firstKey.localeCompare(secondKey);
      },
    );

    const nextSearchParams = new URLSearchParams();
    sortedSearchParams.forEach(([key, value]) => {
      nextSearchParams.append(key, value);
    });

    const nextSearch = nextSearchParams.toString();
    return comparableUrl.pathname + (nextSearch ? `?${nextSearch}` : '') + comparableUrl.hash;
  }

  isSameNavigationEntry(firstUrl, secondUrl) {
    const comparableFirstUrl = this.buildComparableUrl(firstUrl);
    const comparableSecondUrl = this.buildComparableUrl(secondUrl);
    return Boolean(comparableFirstUrl && comparableSecondUrl && comparableFirstUrl === comparableSecondUrl);
  }

  resetHistoryState(url = window.location.href) {
    const currentUrl =
      this.normalizeSameOriginUrl(url) || this.normalizeSameOriginUrl(window.location.href);

    if (!currentUrl) return;

    this.initialUrl = currentUrl;
    this.lastCapturedUrl = currentUrl;

    localStorage.setItem(INITIAL_URL_KEY, currentUrl);
    this.writeHistoryStack([currentUrl]);
    localStorage.removeItem('historyCount');

    if (window.location.pathname === '/') {
      localStorage.removeItem(LAST_ORDERS_PAGE_KEY);
    }
  }

  syncCurrentUrlInHistoryStack(url = window.location.href) {
    const currentUrl =
      this.normalizeSameOriginUrl(url) || this.normalizeSameOriginUrl(window.location.href);

    if (!currentUrl) return;

    if (window.location.pathname === '/') {
      this.resetHistoryState(currentUrl);
      return;
    }

    const stack = this.readHistoryStack();

    if (stack.length > 0) {
      stack[stack.length - 1] = currentUrl;
      this.writeHistoryStack(stack);
    } else {
      this.writeHistoryStack([currentUrl]);
    }

    this.lastCapturedUrl = currentUrl;

    if (!this.initialUrl) {
      this.initialUrl = currentUrl;
      localStorage.setItem(INITIAL_URL_KEY, currentUrl);
      return;
    }

    if (this.isSameNavigationEntry(this.initialUrl, currentUrl)) {
      this.initialUrl = currentUrl;
      localStorage.setItem(INITIAL_URL_KEY, currentUrl);
    }
  }

  initializeHistoryStack() {
    const currentUrl = this.normalizeSameOriginUrl(window.location.href) || window.location.href;

    if (window.location.pathname === '/') {
      this.resetHistoryState(currentUrl);
      return;
    }

    // A fresh session has no same-origin referrer (deep link, app open, direct nav).
    const isNewSession =
      !document.referrer ||
      !document.referrer.startsWith(window.location.origin);

    if (isNewSession) {
      this.resetHistoryState(currentUrl);
    } else {
      let stack = this.readHistoryStack();

      if (!stack.length) {
        stack = [currentUrl];
      } else if (stack[stack.length - 1] !== currentUrl) {
        stack.push(currentUrl);
      } else {
        stack[stack.length - 1] = currentUrl;
      }

      stack = this.writeHistoryStack(stack);

      const storedInitialUrl = this.normalizeSameOriginUrl(localStorage.getItem(INITIAL_URL_KEY));
      this.initialUrl = storedInitialUrl || stack[0] || currentUrl;

      if (this.isSameNavigationEntry(this.initialUrl, currentUrl)) {
        this.initialUrl = currentUrl;
      }

      localStorage.setItem(INITIAL_URL_KEY, this.initialUrl);
      this.lastCapturedUrl = currentUrl;
    }

    // Track the orders list page for detail→list back navigation.
    if (window.location.pathname === '/pages/orders') {
      if (!document.referrer || !document.referrer.includes('/account/orders/')) {
        localStorage.setItem(LAST_ORDERS_PAGE_KEY, currentUrl);
      }
    }
    if (
      window.location.pathname.startsWith('/account/orders/') &&
      document.referrer.includes('/pages/orders')
    ) {
      localStorage.setItem(LAST_ORDERS_PAGE_KEY, document.referrer);
    }
  }

  startUrlWatcher() {
    // History wrappers keep the current entry synchronized without polling.
    this.syncCurrentUrlInHistoryStack(window.location.href);
  }

  isMobileMenuOpen() {
    const bodyEl = document.body;
    return Boolean(bodyEl && bodyEl.classList.contains('reveal-mobile-nav'));
  }

  closeMobileMenu() {
    const bodyEl = document.body;
    if (!bodyEl) return;

    bodyEl.classList.remove(
      'enable-mobile-nav-transition',
      'reveal-mobile-nav',
      'reveal-mobile-nav--revealed',
    );
  }

  activateOverlayHistory(type) {
    if (this.isWebview || !type || this._overlayHistoryType) return;

    try {
      this._ajaxPushCountEnabled = false;
      window.history.pushState({ [OVERLAY_HISTORY_STATE_KEY]: type }, '', window.location.href);
      this._overlayHistoryType = type;
    } catch (_) {
      // no-op
    } finally {
      this._ajaxPushCountEnabled = true;
    }
  }

  releaseOverlayHistory(type) {
    if (!type) return;

    if (this._overlayHistorySkipReleaseType === type) {
      this._overlayHistorySkipReleaseType = null;
      return;
    }

    if (this.isWebview || this._overlayHistoryType !== type) return;

    this._overlayHistoryType = null;

    const currentStateType = window.history.state?.[OVERLAY_HISTORY_STATE_KEY];
    if (currentStateType !== type) return;

    this._ignoreNextPopstate = true;

    try {
      window.history.back();
      window.setTimeout(() => {
        if (this._ignoreNextPopstate) {
          this._ignoreNextPopstate = false;
        }
      }, 300);
    } catch (_) {
      this._ignoreNextPopstate = false;
    }
  }

  closeOverlayFromHistory(type, closeFn) {
    if (this._overlayHistoryType !== type) return false;

    this._overlayHistoryType = null;
    this._overlayHistorySkipReleaseType = type;
    closeFn();
    return true;
  }

  detectWebview() {
    // React Native / Expo WebView — definitive check (no UA sniffing needed).
    if (window.ReactNativeWebView) return true;

    // Flutter / custom WKWebView bridge.
    if (typeof window.__pop_screen__ === 'function') return true;

    const ua = navigator.userAgent || navigator.vendor || window.opera;

    // Android WebView: "wv" flag in UA, or Version/X.Y without Chrome.
    const isAndroid = /Android/.test(ua);
    if (
      isAndroid &&
      (/wv/.test(ua) || (/Version\/\d+\.\d+/.test(ua) && !/Chrome/.test(ua)))
    ) {
      return true;
    }

    const isIOS = /iPhone|iPod|iPad/.test(ua);
    const isSafariLikeBrowser =
      /Safari/.test(ua) || /CriOS|FxiOS|EdgiOS/.test(ua);
    if (
      isIOS &&
      !isSafariLikeBrowser &&
      (navigator.standalone === false || /AppleWebKit/.test(ua))
    ) {
      return true;
    }

    return false;
  }

  setupEventListeners() {
    // Popstate fires when the browser navigates its own history (non-webview only).
    // In a webview the native side owns the back stack; we navigate via message events.
    if (!this.isWebview) {
      window.addEventListener('popstate', (event) => {
        // Let main.js handle AJAX filter popstate events ({path: ...} state).
        if (event.state && event.state.path) return;

        // Suppress the popstate fired by revivalBack's history.go(-1).
        if (this._ignoreNextPopstate) {
          this._ignoreNextPopstate = false;
          return;
        }

        // Cart is open: consume the overlay state and close the drawer.
        if (
          window.RSReactCartEnabled &&
          this._reactCartOpen &&
          this.closeOverlayFromHistory(OVERLAY_HISTORY_TYPES.CART_DRAWER, () => {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
          })
        ) {
          return;
        }

        const legacyCart = document.querySelector('cart-drawer');
        if (
          legacyCart &&
          legacyCart.getAttribute('aria-hidden') === 'false' &&
          this.closeOverlayFromHistory(OVERLAY_HISTORY_TYPES.CART_DRAWER, () => {
            if (typeof legacyCart.close === 'function') legacyCart.close();
          })
        ) {
          return;
        }

        if (
          this.isMobileMenuOpen() &&
          this.closeOverlayFromHistory(OVERLAY_HISTORY_TYPES.MOBILE_MENU, () => {
            this.closeMobileMenu();
          })
        ) {
          return;
        }

        if (this.neutralizeBackEnabled) {
          // Re-push to keep the URL frozen and invoke the registered callback.
          this._ajaxPushCountEnabled = false;
          window.history.pushState(null, '', window.location.href);
          this._ajaxPushCountEnabled = true;
          if (typeof this._neutralizeCallback === 'function') {
            this._neutralizeCallback();
          }
          return;
        }

        // Orders pagination popstates are handled by marmeto-order-listing's own
        // popstate listener; skip historyStack sync here so rapid swipe-backs
        // through pages don't exhaust the stack prematurely.
        if (window.location.pathname === '/pages/orders') return;

        // Native browser back is already navigating; sync our stack.
        // Guard: if the URL after popstate has the same pathname as the current stack
        // top, this is a same-document AJAX filter/pagination back (pushState from
        // main.js or similar), not a real page back — don't pop our stack.
        const stack = this.readHistoryStack();
        if (stack.length > 1) {
          try {
            const stackTopPathname = new URL(stack[stack.length - 1]).pathname;
            if (window.location.pathname === stackTopPathname) return;
          } catch (_) {}
          stack.pop();
          this.writeHistoryStack(stack);
        }
      });
    }

    // Hardware back button on Android (Cordova / Capacitor).
    document.addEventListener('backbutton', (event) => {
      event.preventDefault();
      this.handleBackNavigation();
    });

    // Track React cart open/close state via its document-level custom events.
    // react-cart-drawer.liquid dispatches these on `document`.
    // Also reset on `cart-drawer:closed` which React fires from its own close button/backdrop
    // (that path does NOT dispatch `dispatch:cart-drawer:close`).
    document.addEventListener('dispatch:cart-drawer:open', () => {
      this._reactCartOpen = true;
    });
    document.addEventListener('dispatch:cart-drawer:close', () => {
      this._reactCartOpen = false;
    });
    document.addEventListener('cart-drawer:closed', () => {
      this._reactCartOpen = false;
    });
    document.addEventListener('on:cart-drawer:after-open', () => {
      this.activateOverlayHistory(OVERLAY_HISTORY_TYPES.CART_DRAWER);
    });
    document.addEventListener('on:cart-drawer:after-close', () => {
      this.releaseOverlayHistory(OVERLAY_HISTORY_TYPES.CART_DRAWER);
    });

    this._mobileMenuOpen = this.isMobileMenuOpen();
    const mobileMenuObserver = new MutationObserver(() => {
      const isMenuOpen = this.isMobileMenuOpen();

      if (isMenuOpen === this._mobileMenuOpen) return;

      this._mobileMenuOpen = isMenuOpen;

      if (isMenuOpen) {
        this.activateOverlayHistory(OVERLAY_HISTORY_TYPES.MOBILE_MENU);
        return;
      }

      this.releaseOverlayHistory(OVERLAY_HISTORY_TYPES.MOBILE_MENU);
    });

    if (document.body) {
      mobileMenuObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    // Messages from the native app.
    // React Native sends to window (not document); use window.addEventListener.
    // Use event.data first; fall back to event.nativeEvent?.data for older RN.
    window.addEventListener('message', (event) => {
      const data = event.data ?? event.nativeEvent?.data;
      if (data === 'Pop Screen') {
        this.handleBackNavigation();
        return;
      }
      if (data === 'checkout_complete') {
        console.log('checkout_complete received');
        fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : Promise.reject('Failed to clear the cart')))
          .then((d) => console.log('Cart cleared:', d))
          .catch((e) => console.error('Error clearing cart:', e));
        window.location.href = '/pages/orders';
      }
    });
  }

  handleBackNavigation() {
    // 1. Close any open overlay first — never navigate while a drawer is visible.
    //    React cart: window.RSReactCartEnabled is true when it's active (legacy cart-drawer
    //    is removed from the DOM by react-cart-drawer.liquid when the React cart loads).
    //    React cart events go via `document`, not `window`.
    const isMenuOpen = this.isMobileMenuOpen();

    if (window.RSReactCartEnabled) {
      if (this._reactCartOpen) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
        return;
      }
    } else {
      const cartDrawer = document.querySelector('cart-drawer');
      const isShopifyCartOpen = cartDrawer && cartDrawer.getAttribute('aria-hidden') === 'false';
      if (isShopifyCartOpen) {
        if (typeof cartDrawer.close === 'function') cartDrawer.close();
        return;
      }
    }

    if (isMenuOpen) {
      this.closeMobileMenu();
      return;
    }

    // 2. Homepage → exit the webview (or go home as final fallback in browser).
    if (window.location.pathname === '/') {
      this.closeWebview();
      return;
    }

    // 3. Redirect-flag pages → replace history entry with home (no back loop).
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('utm_source') === 'csredirect') {
      this.navigateWithPreservedDeliveryCity('/', { replace: true });
      return;
    }

    // 4. Order detail page → back to the list page the user navigated from.
    if (window.location.pathname.startsWith('/account/orders/')) {
      const lastOrdersPage =
        localStorage.getItem(LAST_ORDERS_PAGE_KEY) || '/pages/orders?page=0';
      this.navigateWithPreservedDeliveryCity(lastOrdersPage);
      return;
    }

    // 5. Orders list pagination → AJAX-load the previous page without a full reload.
    //    For page 0, exit the orders section via the historyStack directly so we
    //    bypass any stale pagination entries that marmeto's pushState may have added
    //    to the browser history during forward navigation.
    if (window.location.pathname === '/pages/orders') {
      const page = parseInt(urlParams.get('page') || '0', 10);
      if (page > 0) {
        const prevPage = page - 1;
        const orderListing = document.querySelector('marmeto-order-listing');
        if (orderListing && typeof orderListing.getOrders === 'function') {
          // Pre-update the URL to the target page BEFORE the async fetch.
          // updateUrlWithPageNumber (called inside getOrders) checks whether the URL
          // already shows the target page; if so it uses replaceState, avoiding a
          // new browser history entry during backward navigation.
          const targetUrl = new URL(window.location.href);
          targetUrl.searchParams.set('page', prevPage);
          window.history.replaceState({}, '', targetUrl.toString());
          orderListing.getOrders(prevPage, orderListing.pageSize, { scrollToTop: true });
        } else {
          // Component not yet upgraded — fall back to full navigation.
          this.navigateWithPreservedDeliveryCity(`/pages/orders?page=${prevPage}`);
        }
        return;
      }
      // page === 0: navigate out of orders using the historyStack directly.
      // history.back() is intentionally avoided here: browser history can contain
      // stale pagination entries from marmeto's pushState calls, and would land
      // the user back on a previous orders page instead of exiting the section.
      const stack = this.readHistoryStack();
      if (stack.length > 1) {
        stack.pop();
        this.writeHistoryStack(stack);
        this.navigateWithPreservedDeliveryCity(stack[stack.length - 1]);
      } else {
        this.navigateWithPreservedDeliveryCity('/');
      }
      return;
    }

    // 6. Entry-point page (deep-link arrival) → always go home, not further back.
    if (
      this.initialUrl &&
      this.isSameNavigationEntry(window.location.href, this.initialUrl) &&
      window.location.pathname !== '/'
    ) {
      this.navigateWithPreservedDeliveryCity('/');
      return;
    }

    // 7. In-session navigation: use bfcache-friendly history.go(-1).
    //    Same-pathname pushState calls (AJAX filters, orders pagination) were
    //    converted to replaceState at the source, so there are no ghost entries
    //    between the current page and the real previous entry.  history.go(-1)
    //    will always land on the correct cross-document entry.
    //    A 300ms fallback handles the rare out-of-bounds case (user arrived via
    //    direct deep-link and has no real previous entry in this session).
    const stack = this.readHistoryStack();
    if (stack.length > 1) {
      stack.pop();
      this.writeHistoryStack(stack);
      const targetUrl = stack[stack.length - 1];

      // Suppress our own popstate listener — stack already synced above.
      this._ignoreNextPopstate = true;

      // If history.go(-1) turns out to be out-of-bounds (no earlier entry),
      // the fallback navigates explicitly.
      clearTimeout(this._backFallbackTimer);
      this._backFallbackTimer = setTimeout(() => {
        this._backFallbackTimer = null;
        if (window.location.href !== targetUrl) {
          this._ignoreNextPopstate = false;
          this.navigateWithPreservedDeliveryCity(targetUrl);
        }
      }, 300);

      window.history.go(-1);
      return;
    }

    // 8. Final fallback.
    this.navigateWithPreservedDeliveryCity('/');
  }

  closeWebview() {
    // Clean up all navigation state.
    localStorage.removeItem(INITIAL_URL_KEY);
    localStorage.removeItem(HISTORY_STACK_KEY);
    localStorage.removeItem(LAST_ORDERS_PAGE_KEY);
    // Remove legacy keys from previous versions.
    localStorage.removeItem('historyCount');

    // React Native bridge.
    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage('CLOSE_WEBVIEW');
        return;
      }
    } catch (ex) {
      console.error('ReactNativeWebView error:', ex);
    }

    // Flutter / custom WKWebView bridge.
    try {
      if (typeof window.__pop_screen__ === 'function') {
        window.__pop_screen__();
        return;
      }
    } catch (ex) {
      console.error('Flutter bridge error:', ex);
    }

    // Popup / SFSafariViewController.
    if (window.opener) {
      try {
        window.opener.postMessage('CLOSE_WEBVIEW', '*');
        window.close();
        return;
      } catch (_) {}
    }

    // Non-webview fallback — navigate home.
    window.location.href = '/';
  }

  // Based on CultsportPLPV3Widget.tsx (lines 219-230)
  neutralizeBack(callback) {
    this.neutralizeBackEnabled = true;
    this._neutralizeCallback = callback || null;
    this._ajaxPushCountEnabled = false;
    window.history.pushState(null, '', window.location.href);
    this._ajaxPushCountEnabled = true;
  }

  revivalBack() {
    this.neutralizeBackEnabled = false;
    this._neutralizeCallback = null;
    // Suppress the popstate that history.go(-1) will fire to avoid re-entering handleBackNavigation.
    this._ignoreNextPopstate = true;
    window.history.go(-1);
  }
}

// Initialize the handler
const backNavigation = new BackNavigationHandler();
backNavigationInstance = backNavigation;

// Execute any pending calls
while (pendingCalls.length > 0) {
  pendingCalls.pop();
  backNavigation.handleBackNavigation();
}

// Keep the old function for backwards compatibility
function handleBackNavigation() {
  backNavigation.handleBackNavigation();
}